import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function Tempo100() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Hastighed og kørsel", path: "/trailer-hastighed" },
    { name: "Tempo 100", path: "/tempo-100" }
  ];
  const tocItems = [
    { id: "hvad-er-tempo-100", title: "Hvad er Tempo 100 i Danmark?" },
    { id: "tekniske-krav-trailer", title: "Tekniske krav til traileren" },
    { id: "krav-til-traekbil", title: "Krav og vægtforhold for trækbilen" },
    { id: "syn-og-registrering", title: "Sådan får du traileren Tempo 100-godkendt" },
    { id: "tempo-100-udland", title: "Gælder Tempo 100 i udlandet? (Tyskland & Norge)" }
  ];

  const faq = [
    {
      q: "Gælder en dansk Tempo 100-godkendelse i Tyskland?",
      a: "Nej, en dansk Tempo 100-godkendelse gælder ikke på tyske motorveje. For at køre 100 km/t i Tyskland skal traileren have en særskilt tysk Tempo 100-godkendelse med tysk mærkat. Den danske godkendelse anerkendes dog i Norge."
    },
    {
      q: "Hvor gamle må dækkene højst være på en Tempo 100-trailer?",
      a: "Dækkene på traileren må højst være 6 år gamle, regnet fra fremstillingsdatoen (DOT-koden på dækkets side). Hvis dækkene bliver ældre end 6 år, bortfalder Tempo 100-godkendelsen automatisk, indtil der monteres nye dæk."
    },
    {
      q: "Skal traileren til periodisk syn, hvis den er Tempo 100-godkendt?",
      a: "Ja. Hvis trailerens samlede tilladte akseltryk overstiger 750 kg, indkaldes den automatisk til periodisk syn hvert 2. år hos Færdselsstyrelsen. For lette trailere på 750 kg eller derunder er der intet periodisk syn."
    },
    {
      q: "Hvad koster det at få en trailer Tempo 100-godkendt?",
      a: "Et Tempo 100-syn hos en godkendt synshal koster typisk mellem 450 og 850 kr. afhængigt af synshallen, plus gebyret for det officielle mærkat og registrering i Motorregistret."
    }
  ];

  const sources = [
    { title: "Bekendtgørelse om Tempo 100 for påhængskøretøjer (BEK nr. 1614 af 13/12/2016)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2016/1614" },
    { title: "Færdselsstyrelsen: Krav og regler for Tempo 100 påhængskøretøjer", source: "fstyr.dk", url: "https://fstyr.dk/privat/krav-til-koeretoejer/tempo-100" },
    { title: "Færdselsloven § 43, stk. 3 (LBK nr. 1324 af 18/09/2023)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" }
  ];

  return (
    <>
      <SEO 
        title="Tempo 100 trailer: Regler, krav og godkendelse" 
        description="Alt om Tempo 100 til trailer i Danmark: tekniske krav til dæk, støddæmpere og trækbil, synsfrister samt forskellen på dansk og tysk Tempo 100." 
        pathname="/tempo-100"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Tempo 100 til trailer: Krav, syn og regler
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          En dansk Tempo 100-godkendelse giver tilladelse til at køre op til <strong>100 km/t på danske motorveje</strong> med et vogntog. Uden denne godkendelse er den maksimale tilladte hastighed 80 km/t.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          For at opnå godkendelsen skal traileren opfylde en række strenge tekniske krav ved et syn: Dækkene må højst være 6 år gamle, traileren skal have hydrauliske støddæmpere (hvis bremset), og trækbilen skal have ABS-bremser. Den danske godkendelse gælder ikke i Tyskland, men anerkendes i Norge.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Vigtigste krav til Tempo 100">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Dæk højst <strong>6 år gamle</strong> (DOT-mærkning)</li>
            <li>• Hastighedsmærkning mindst <strong>120 km/t (indeks L)</strong></li>
            <li>• Hydrauliske støddæmpere på hjulakslen (ved bremsede trailere)</li>
            <li>• Trækbil skal have <strong>ABS-bremser</strong></li>
          </ul>
        </Callout>

        <h2 id="hvad-er-tempo-100" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvad er Tempo 100 i Danmark?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Den danske Tempo 100-ordning trådte i kraft den 1. juli 2016 for påhængskøretøjer. Ordningen tillader, at godkendte personbiler og varebiler må fremføre et godkendt påhængskøretøj med op til 100 km/t på motorveje.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          På andre veje end motorveje (landeveje, motortrafikveje og byveje) ændrer Tempo 100 ingenting. Her gælder de sædvanlige grænser på højst 80 km/t uden for byzone og 50 km/t i byzone. Læs alle grænser i vores artikel om <Link to="/trailer-hastighed" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer hastighed</Link>.
        </p>

        <h2 id="tekniske-krav-trailer" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Tekniske krav til traileren
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          For at et påhængskøretøj kan godkendes til Tempo 100 hos en synsvirksomhed, skal det opfylde følgende krav i Færdselsstyrelsens forskrifter:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Komponent</th>
                <th className="py-2.5 pr-4 font-semibold">Lovkrav for Tempo 100</th>
                <th className="py-2.5 font-semibold">Kontrolmetode</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Dækkenes alder</td>
                <td className="py-3 pr-4">Maksimalt 6 år gamle</td>
                <td className="py-3 text-[#5C636B]">Aflæses på 4-cifret DOT-kode</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Hastighedsmærkning</td>
                <td className="py-3 pr-4">Mindst 120 km/t (kategori L)</td>
                <td className="py-3 text-[#5C636B]">Bogstavmærke på dæksiden</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Bæreevne (Load Index)</td>
                <td className="py-3 pr-4">Skal svare til tilladt akseltryk</td>
                <td className="py-3 text-[#5C636B]">Belastningstal på dæksiden</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Støddæmpere</td>
                <td className="py-3 pr-4">Hydrauliske støddæmpere på alle hjul</td>
                <td className="py-3 text-[#5C636B]">Gælder for bremsede trailere</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Bremseanlæg</td>
                <td className="py-3 pr-4">Påløbsbremse med bakautomatik</td>
                <td className="py-3 text-[#5C636B]">Hvis totalvægt overstiger 750 kg</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs mere om dækkoder og kontrol i vores guide om <Link to="/trailer-daek" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerdæk og dæktryk</Link>.
        </p>

        <h2 id="krav-til-traekbil" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Krav og vægtforhold for trækbilen
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Det er ikke kun traileren, der skal godkendes; trækbilen skal opfylde bestemte sikkerhedskrav, hver gang du kører 100 km/t:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Bilen skal have en tilladt totalvægt på <strong>højst 3.500 kg</strong>.</li>
          <li>Bilen skal være udstyret med <strong>ABS-bremser</strong>.</li>
          <li><strong>Vægtforhold:</strong> Trailerens tilladte totalvægt må højst udgøre en vis faktor af trækbilens køreklare vægt (for almindelige trailere med bremser og støddæmpere er faktoren typisk 1,1; med stabilisatorkobling op til 1,2; for ubremsede trailere højst 0,3).</li>
        </ul>

        <h2 id="syn-og-registrering" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sådan får du traileren Tempo 100-godkendt
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          For at få Tempo 100 følges denne fremgangsmåde:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Forberedelse:</strong> Tjek dækkenes DOT-alder (under 6 år) og at hydrauliske støddæmpere er monteret på akslen.</li>
          <li><strong>Syn:</strong> Kør traileren til en godkendt synshal og bed om et Tempo 100-syn.</li>
          <li><strong>Attest og mærkat:</strong> Synshallen registrerer godkendelsen i Motorregistret (DMR) og udleverer den officielle Tempo 100-godkendelsesattest samt et rundt dansk Tempo 100-mærke med statsligt hologram.</li>
          <li><strong>Montering:</strong> Monter mærkatet bagpå traileren, så det er synligt lige bagfra.</li>
          <li><strong>Medbring attesten:</strong> Tempo 100-godkendelsesattesten skal <em>altid medbringes</em> i bilen under kørsel.</li>
        </ol>

        <h2 id="tempo-100-udland" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Gælder Tempo 100 i udlandet? (Tyskland &amp; Norge)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Her sker de fleste misforståelser:
        </p>

        <div className="my-6 p-4 border border-[#E4E1D8] bg-[#FBFAF6] rounded-[4px] space-y-3">
          <div>
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              Tyskland (Ikke gyldig)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Det danske Tempo 100-mærke <strong>gælder ikke i Tyskland</strong>. Kører du ind i Tyskland, må du kun køre 80 km/t på Autobahn, medmindre du særskilt har fået traileren godkendt til tysk Tempo 100 hos f.eks. DEKRA eller TÜV. Læs mere i guiden om <Link to="/trailer-i-tyskland" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i Tyskland</Link>.
            </p>
          </div>
          <div className="border-t border-[#E4E1D8] pt-3">
            <h3 className="text-[16px] font-bold text-[#2E3A46] mb-1 font-mono uppercase">
              Norge (Gensidig anerkendelse)
            </h3>
            <p className="text-[15px] text-[#1A1C1F] m-0">
              Danmark og Norge har en gensidig anerkendelsesaftale. En dansk Tempo 100-godkendelse anerkendes fuldt ud til 100 km/t-kørsel på norske motorveje, og norske Tempo 100-trailere må køre 100 km/t i Danmark.
            </p>
          </div>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Husk også, at godkendelsen medfører <strong>periodisk syn hvert 2. år</strong> for trailere over 750 kg. Se <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">regler for syn af trailer</Link> og <Link to="/trailer-regler" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailerregler i Danmark</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
